import type { ReactNode } from "react";
import { Link } from "react-router";
import type { CompanyRow } from "@/lib/staff-api";
import type { SortDir } from "@/lib/list-url";
import { formatDate, formatPhone } from "@/lib/formatters";
import { SortableTable, type Column } from "./SortableTable";

const HIDDEN_TAG = "ml-2 rounded-full bg-gray-700 px-2 py-0.5 text-xs font-medium text-white";

interface CompaniesTableProps<T extends CompanyRow> {
  caption: string;
  rows: T[];
  sort: string;
  dir: SortDir;
  onSort: (key: string) => void;
  /** A line under the company name (search: what the query matched). */
  note?: (row: T) => ReactNode;
}

/** The company lookup table: the Companies tab and search. */
export function CompaniesTable<T extends CompanyRow>({ caption, rows, sort, dir, onSort, note }: CompaniesTableProps<T>): React.ReactElement {
  const columns: Column<T>[] = [
    {
      key: "name",
      label: "Company",
      render: (r) => (
        <span>
          <Link to={`/staff/companies/${encodeURIComponent(r.id)}`} className="font-medium text-blue-700 hover:text-blue-900 hover:underline">
            {r.companyName}
          </Link>
          {r.hidden && <span className={HIDDEN_TAG}>Hidden</span>}
          {note && <span className="block text-xs text-gray-600">{note(r)}</span>}
        </span>
      ),
    },
    {
      key: "contact",
      label: "Contact",
      render: (r) => (
        <span>
          {r.contactName}
          <span className="block text-gray-600">{formatPhone(r.contactPhone)}</span>
        </span>
      ),
    },
    {
      key: "email",
      label: "Email",
      render: (r) => (
        <a href={`mailto:${r.email}`} className="text-blue-700 hover:text-blue-900 hover:underline">
          {r.email}
        </a>
      ),
    },
    { key: "employees", label: "Employees", align: "right", render: (r) => r.employeeCount },
    { key: "waiting", label: "Waiting on you", align: "right", render: (r) => r.waitingOnYou },
    { key: "lastOrder", label: "Last order", render: (r) => (r.lastOrderAt ? formatDate(r.lastOrderAt) : "—") },
    { key: "registered", label: "Registered", render: (r) => formatDate(r.createdAt) },
  ];
  return <SortableTable caption={caption} columns={columns} rows={rows} rowKey={(r) => r.id} sort={sort} dir={dir} onSort={onSort} />;
}
